import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { EVENTS_TYPES } from "./useParentCommunication";

/**
 * Hook to synchronize routing between parent window and child iframe
 * 
 * This hook:
 * 1. Notifies parent when child route changes (CHILD → PARENT)
 * 2. Navigates when parent sends navigation commands (PARENT → CHILD)
 * 3. Prevents infinite loops by tracking navigation source
 */
export const useRouteSync = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isInIframe = window.parent !== window;
  const navigationSourceRef = useRef<"parent" | "child" | null>(null);

  // CHILD → PARENT: Notify parent of route changes
  useEffect(() => {
    if (!isInIframe) return;

    // Only send to parent if navigation originated from child
    if (navigationSourceRef.current === "parent") {
      navigationSourceRef.current = null;
      return;
    }

    const path = location.pathname + location.search;
    
    window.parent.postMessage(
      { 
        type: EVENTS_TYPES.CHILD_ROUTE_CHANGE, 
        path 
      },
      "*"
    );

    navigationSourceRef.current = null;
  }, [location, isInIframe]);

  // PARENT → CHILD: Handle navigation commands from parent
  useEffect(() => {
    if (!isInIframe) return;

    const handleParentNavigation = (event: MessageEvent) => {
      if (event.data?.type === EVENTS_TYPES.PARENT_NAVIGATE) {
        const targetPath = event.data.path;
        const currentPath = location.pathname + location.search;

        // Only navigate if the path is different
        if (targetPath !== currentPath) {
          navigationSourceRef.current = "parent";
          navigate(targetPath);
        }
      }
    };

    window.addEventListener("message", handleParentNavigation);
    
    return () => {
      window.removeEventListener("message", handleParentNavigation);
    };
  }, [location, navigate, isInIframe]);
};
